// F7 — protein distribution across the day. Total protein is only half the
// story for anyone training: the same 150 g lands differently spread over
// four meals than dumped into dinner. Purely descriptive — it shows the
// spread and the per-kg figure, and never scolds anyone for either.
import { useT } from "../lib/i18n";
import { SLOT_ORDER } from "../lib/meals";
import { SheetShell } from "./SheetShell";
import type { FoodEntry, MealSlot } from "@shared/types";

interface Props {
  entries: FoodEntry[];
  targetG: number;
  weightKg: number | null;
  onClose: () => void;
}

export function ProteinSpreadSheet({ entries, targetG, weightKg, onClose }: Props) {
  const t = useT();

  const bySlot = new Map<MealSlot | "other", number>();
  for (const e of entries) {
    const key = (e.plan_meal_slot ?? "other") as MealSlot | "other";
    bySlot.set(key, (bySlot.get(key) ?? 0) + (e.protein_g ?? 0));
  }
  const total = [...bySlot.values()].reduce((a, b) => a + b, 0);
  const peak = Math.max(1, ...bySlot.values());
  const slots: Array<MealSlot | "other"> = [...SLOT_ORDER, "other"];
  const perKg = weightKg ? total / weightKg : null;

  return (
    <SheetShell onClose={onClose}>
      <h3 className="text-lg font-semibold mt-4 mb-1">{t("protein.spread_title")}</h3>
      <p className="text-sm text-fg-muted mb-4">{t("protein.spread_hint")}</p>

      <div className="space-y-2.5 mb-4">
        {slots.map((slot) => {
          const g = Math.round(bySlot.get(slot) ?? 0);
          if (slot === "other" && g === 0) return null;
          return (
            <div key={slot}>
              <div className="flex items-baseline justify-between text-sm">
                <span>{slot === "other" ? t("protein.unassigned") : t(`meal.header.${slot}`)}</span>
                <span className="text-fg-muted tabular-nums">{g} g</span>
              </div>
              <div className="h-1.5 rounded-full bg-surface-strong overflow-hidden mt-1">
                <div
                  className="h-full rounded-full bg-accent-protein transition-[width] duration-500"
                  style={{ width: `${(g / peak) * 100}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>

      <div className="bg-surface rounded-2xl p-4 space-y-1">
        <div className="text-2xl font-semibold tabular-nums">
          {Math.round(total)} g
          <span className="text-sm text-fg-muted ml-2">/ {targetG} g</span>
        </div>
        {perKg !== null && (
          <p className="text-sm text-fg-muted">
            {t("protein.per_kg", { n: perKg.toFixed(1) })}
          </p>
        )}
        <p className="text-sm text-fg-muted leading-relaxed pt-1">{t("protein.guidance")}</p>
      </div>
      <div className="pb-2" />
    </SheetShell>
  );
}
